import { EmployeePulseInsightsGroupsDocument } from 'generated/resources/graphql'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { useQuery } from 'modules/core/hooks/useQuery'

import type { GroupByType } from 'modules/performance_analytics/components/helpers'

import { useProcessChartData } from 'modules/performance_analytics/components/EmployeePulseInsights/lib/dataProcessing'
import {
  MOOD_CATEGORIES,
  type ProcessedChartData,
} from 'modules/performance_analytics/components/EmployeePulseInsights/types'

interface UseEmployeePulseDataProps {
  groupBy?: GroupByType
}

interface UseEmployeePulseDataReturn {
  processedData: ProcessedChartData | null
  loading: boolean
  getFiltersVariables: ReturnType<typeof useFilters>['getFiltersVariables']
  availableFilters: ReturnType<typeof useFilters>['availableFilters']
  enabledFilters: ReturnType<typeof useFilters>['filters']
  onAddFilter: ReturnType<typeof useFilters>['onAddFilter']
  onUpdateFilter: ReturnType<typeof useFilters>['onUpdateFilter']
  dataTableFilters: Record<string, unknown>
  hasRecords: boolean
}

export const useEmployeePulseData = ({
  groupBy,
}: UseEmployeePulseDataProps): UseEmployeePulseDataReturn => {
  const {
    getFiltersVariables,
    availableFilters,
    filters: enabledFilters,
    onAddFilter,
    onUpdateFilter,
  } = useFilters()

  const dataTableFilters = JSON.parse(JSON.stringify(getFiltersVariables().filter)) ?? {}

  const { data: queryResponse, loading } = useQuery(EmployeePulseInsightsGroupsDocument, {
    variables: {
      employeeIds: dataTableFilters?.employeeId?.in,
      managerIds: dataTableFilters?.managerId?.in,
      teamIds: dataTableFilters?.teamId?.in,
      tenureDate: dataTableFilters?.tenureDate?.in,
      potentialScoreRange: dataTableFilters?.potentialScoreRange?.in,
      scoreRange: dataTableFilters?.scoreRange?.in,
      gridStatus: dataTableFilters?.gridStatus?.in,
      genders: dataTableFilters?.gender?.in,
      workspaceIds: dataTableFilters?.workspace?.in,
      legalEntityIds: dataTableFilters?.legalEntityId?.in,
      nationalities: dataTableFilters.nationality?.in,
      groupBy: groupBy ?? 'gender',
      includeGroups: !!groupBy,
      projectIds: dataTableFilters?.project?.in,
      singleValueInsight: !groupBy,
    },
  })

  const processedData = useProcessChartData(queryResponse, groupBy, MOOD_CATEGORIES)
  const { loadingLookups } = processedData

  const hasRecords = true

  return {
    hasRecords,
    processedData,
    loading: loading || loadingLookups,
    getFiltersVariables,
    availableFilters,
    enabledFilters,
    onAddFilter,
    onUpdateFilter,
    dataTableFilters,
  }
}
